import { formatPlainNumber, toNumber } from "../../../lib/formatters.ts";
import {
	MAX_SIZE_COST_RATE,
	MIN_ORDER_NOTIONAL_USD,
} from "../../../lib/trade-constants.ts";
import {
	isMarketOrderType,
	isTwapOrderType,
	type MarginMode,
	type OrderSide,
	type OrderType,
} from "../constants/order-form.constants.ts";
import type { OrderDraft } from "./order-form-model.ts";

type DeriveOrderValuesParams = {
	draft: OrderDraft;
	markPrice: number;
	autoQuotePrice?: string;
	selectedLeverage: number;
	availableBalance: number;
	szDecimals: number;
	side: OrderSide;
	currentPositionLiquidationPx: string | number | null | undefined;
	orderType: OrderType;
	marginMode: MarginMode;
	maxLeverage: number;
	/** Spendable account equity backing Cross positions (spot USDC for unified accounts). */
	perpAccountValue: string | number | null | undefined;
	/** Signed existing position size (szi): positive long, negative short. */
	currentPositionSize: string | number | null | undefined;
	/** Existing position average entry price. */
	currentPositionEntryPx: string | number | null | undefined;
	/**
	 * Server max trade size for the active side (`activeAssetData.maxTradeSzs`),
	 * in coin units. HL margins orders at mark, so this is the binding size cap.
	 */
	maxSizeCap?: number | null;
};

export type DerivedOrderValues = {
	displayPrice: string;
	displayAmount: string;
	displayQuoteAmount: string;
	displayPercentage: number;
	explicitPrice: number;
	referencePrice: number;
	sizeNum: number;
	orderValue: number;
	marginRequired: number;
	estimatedLiquidation: number;
	hasPrice: boolean;
	hasSize: boolean;
	aboveMaxNotional: boolean;
};

function calculatePercentage(notional: number, maxNotional: number) {
	if (maxNotional <= 0 || notional <= 0) return 0;
	return Math.min(100, Math.round((notional / maxNotional) * 100));
}

function clampPercentage(value: string) {
	const pct = Math.round(toNumber(value));
	return Math.min(100, Math.max(0, pct));
}

/**
 * Snap a coin size to the market's size grid (10^-szDecimals steps).
 * Float dust is stripped before stepping so an exact grid multiple never
 * shifts a step in "up"/"down" mode.
 */
export function roundSizeToStep(
	size: number,
	szDecimals: number,
	mode: "nearest" | "up" | "down",
): number {
	if (!(size > 0)) return 0;
	const factor = 10 ** szDecimals;
	const scaled = Number((size * factor).toPrecision(12));
	const stepped =
		mode === "up"
			? Math.ceil(scaled)
			: mode === "down"
				? Math.floor(scaled)
				: Math.round(scaled);
	return stepped / factor;
}

function getDisplayPrice(draft: OrderDraft, autoQuotePrice?: string) {
	switch (draft.price.source) {
		case "selected":
			return draft.price.selectedInput;
		case "autoLast":
			return autoQuotePrice ?? "";
		default:
			return draft.price.manualInput;
	}
}

type LiquidationEstimateParams = {
	side: OrderSide;
	/** Absolute size being ordered, in coin units. */
	orderSize: number;
	/** Price the order would fill at (limit price or mark). */
	entryPrice: number;
	/** Current mark price — the reference point for Cross (account equity is measured here). */
	markPrice: number;
	selectedLeverage: number;
	/** Market max leverage → maintenance margin fraction `1 / (2 * maxLeverage)`. */
	maxLeverage: number;
	marginMode: MarginMode;
	/** Spendable account equity backing Cross positions (spot USDC for unified accounts). */
	perpAccountValue: number;
	/** Signed existing position size: positive long, negative short. */
	positionSize: number;
	/** Existing position average entry price. */
	positionEntryPx: number;
	/** Existing position's API-reported liquidation price (fallback when no order is sized). */
	fallbackLiquidationPx: number;
};

/**
 * Projected liquidation price after the order fills, following Hyperliquid's
 * formula (docs → Trading → Liquidations):
 *
 *   liq_price = price − side · margin_available / position_size / (1 − l · side)
 *
 * where `side` is +1 long / −1 short, `l` is the maintenance margin fraction
 * (`1 / (2 · maxLeverage)`), and `margin_available` is account equity minus
 * maintenance margin — for Cross the whole perp account backs it, for Isolated
 * only the position's own initial margin does.
 *
 * Why the result is size-dependent in some modes but not others:
 *   • Isolated: anchored at the position's own (blended) entry; `margin_available`
 *     scales with size, so size cancels and the liq price moves only when the
 *     entry or leverage changes — this matches Hyperliquid, where a fresh isolated
 *     liq price is fixed by entry + leverage, not quantity (tiers approximated away).
 *   • Cross: anchored at the current mark (where `perpAccountValue` is measured —
 *     it already includes the existing position's unrealized PnL). `margin_available`
 *     is whole-account equity and does NOT scale with this position, so a larger
 *     size pushes the liq price toward the mark. Because it needs that equity, when
 *     `perpAccountValue` isn't loaded yet (not connected / unfunded / loading) it
 *     falls back to the isolated estimate as an indicative value rather than "--".
 *
 * Approximations (don't "fix" without API meta): maintenance-margin tiers (mmr
 * rises with notional) use the base `1 / (2 · maxLeverage)`; Cross omits other
 * positions' maintenance margin (their equity is already in `perpAccountValue`).
 */
export function estimateLiquidationPrice({
	side,
	orderSize,
	entryPrice,
	markPrice,
	selectedLeverage,
	maxLeverage,
	marginMode,
	perpAccountValue,
	positionSize,
	positionEntryPx,
	fallbackLiquidationPx,
}: LiquidationEstimateParams): number {
	const safeFallback = fallbackLiquidationPx > 0 ? fallbackLiquidationPx : 0;

	// No order sized → show the existing position's actual liquidation price.
	if (!(orderSize > 0) || !(entryPrice > 0)) return safeFallback;

	const mmr = maxLeverage > 0 ? 1 / (2 * maxLeverage) : 0;

	// ── Project the post-order position (signed: + long, − short) ──
	const signedOrder = side === "buy" ? orderSize : -orderSize;
	const projectedSize = positionSize + signedOrder;

	// Order fully closes the position → nothing left to liquidate.
	if (Math.abs(projectedSize) < 1e-9) return 0;

	const projectedSide = projectedSize > 0 ? 1 : -1;
	const absProjected = Math.abs(projectedSize);
	const absPosition = Math.abs(positionSize);
	const denom = 1 - mmr * projectedSide;
	if (denom === 0) return 0;

	// Cross is backed by the whole account, so it can only be computed once
	// account equity is loaded. `perpAccountValue` is current equity measured at
	// the mark — it ALREADY contains the existing position's unrealized PnL — so
	// the formula must be anchored at the mark, not the entry. The order isn't
	// filled yet, so add its mark-to-fill PnL (`signedOrder · (mark − entryPrice)`,
	// zero for a market order). Using the blended entry here instead would mix two
	// reference points and skew the result whenever an existing position's entry
	// differs from the mark.
	const mark = markPrice > 0 ? markPrice : entryPrice;
	if (marginMode === "Cross" && mark > 0 && perpAccountValue > 0) {
		const equity = perpAccountValue + signedOrder * (mark - entryPrice);
		const marginAvailable = equity - mmr * absProjected * mark;
		const liq = mark - (projectedSide * marginAvailable) / absProjected / denom;
		return liq > 0 ? liq : 0;
	}

	// Isolated is backed only by the position's own margin, with PnL measured from
	// the position's (blended) entry — so size cancels and the liq price tracks
	// entry + leverage, shifting only when adding at a different price blends entry.
	// This also serves as the Cross fallback before account equity is known (not
	// connected / not yet funded / still loading): an indicative entry+leverage
	// estimate instead of "--", which auto-corrects to the true Cross value once
	// `perpAccountValue` loads.
	let projectedEntry: number;
	if (positionSize === 0) {
		projectedEntry = entryPrice;
	} else if (positionSize > 0 === signedOrder > 0) {
		// Adding in the same direction → notional-weighted average entry.
		projectedEntry =
			(absPosition * positionEntryPx + orderSize * entryPrice) /
			(absPosition + orderSize);
	} else if (orderSize <= absPosition) {
		// Reducing → the remaining position keeps its original entry.
		projectedEntry = positionEntryPx > 0 ? positionEntryPx : entryPrice;
	} else {
		// Flipping → the remainder opens at the new order price.
		projectedEntry = entryPrice;
	}

	if (!(projectedEntry > 0) || !(selectedLeverage > 0)) return safeFallback;

	const notional = absProjected * projectedEntry;
	const marginAvailable = notional / selectedLeverage - mmr * notional;
	const liq =
		projectedEntry - (projectedSide * marginAvailable) / absProjected / denom;

	// A negative result means the position is effectively un-liquidatable at the
	// current collateral → surface 0 (rendered as "--"), never a negative price.
	return liq > 0 ? liq : 0;
}

export function deriveOrderValues({
	draft,
	markPrice,
	autoQuotePrice,
	selectedLeverage,
	availableBalance,
	szDecimals,
	side,
	currentPositionLiquidationPx,
	orderType,
	marginMode,
	maxLeverage,
	perpAccountValue,
	currentPositionSize,
	currentPositionEntryPx,
	maxSizeCap,
}: DeriveOrderValuesParams): DerivedOrderValues {
	const displayPrice = getDisplayPrice(draft, autoQuotePrice);
	const explicitPrice = toNumber(displayPrice);
	const referencePrice = explicitPrice || markPrice;
	// `availableToTrade`/`maxTradeSzs` are the exact zero-headroom margin
	// boundary; an order sized right at them is rejected once the fill's
	// fees/spread land. Reserve those leverage-scaled costs up front (see
	// MAX_SIZE_COST_RATE) so 100% survives the fill.
	const costScale = 1 + selectedLeverage * MAX_SIZE_COST_RATE;
	const maxNotional =
		availableBalance > 0 && selectedLeverage > 0
			? (availableBalance * selectedLeverage) / costScale
			: 0;
	// Max affordable size, shared by every size source. HL margins orders at
	// MARK (`size × mark / leverage` — the limit price is irrelevant), so the
	// server-computed `maxTradeSzs` (= capacity × leverage ÷ mark) is the binding
	// cap: a bid below mark affords FEWER coins than `maxNotional / limitPx`
	// suggests, and uncapped it comes back "Insufficient margin to place order".
	const clientMaxSize =
		maxNotional > 0 && referencePrice > 0
			? roundSizeToStep(maxNotional / referencePrice, szDecimals, "down")
			: 0;
	const maxSize =
		maxSizeCap != null && maxSizeCap > 0
			? Math.min(
					clientMaxSize,
					roundSizeToStep(maxSizeCap / costScale, szDecimals, "down"),
				)
			: clientMaxSize;

	let displayAmount = "";
	let displayQuoteAmount = "";
	let displayPercentage = 0;
	let orderValue = 0;
	let aboveMaxNotional = false;

	switch (draft.size.source) {
		case "quote": {
			displayQuoteAmount = draft.size.input;
			const usd = toNumber(displayQuoteAmount);
			if (usd > 0 && referencePrice > 0) {
				let size = roundSizeToStep(usd / referencePrice, szDecimals, "nearest");
				// Typed at/above the exchange minimum but grid rounding dropped the
				// effective notional below it → take the next step up so Hyperliquid
				// doesn't reject the order.
				if (
					usd >= MIN_ORDER_NOTIONAL_USD &&
					size * referencePrice < MIN_ORDER_NOTIONAL_USD
				) {
					size = roundSizeToStep(usd / referencePrice, szDecimals, "up");
				}
				if (maxNotional > 0) {
					const factor = 10 ** szDecimals;
					const stepsOver =
						Math.round(size * factor) - Math.round(maxSize * factor);
					// One step over the max affordable size is a rounding artifact →
					// clamp down. A larger overshoot is caught by the shared guard
					// after the switch (which also covers raw-size input).
					if (stepsOver === 1) size = maxSize;
				}
				displayAmount = size > 0 ? formatPlainNumber(size, szDecimals) : "";
				// Effective notional of the grid-snapped size, so min-notional
				// validation and margin metrics reflect what is actually submitted.
				orderValue = size * referencePrice;
			}
			displayPercentage = calculatePercentage(orderValue, maxNotional);
			break;
		}
		case "percent": {
			displayPercentage = clampPercentage(draft.size.input);
			const targetNotional =
				maxNotional > 0 ? (displayPercentage / 100) * maxNotional : 0;
			// Floor to the size grid so 100% can never exceed buying power, and
			// cap at the (mark-based) max size so a below-mark limit price can't
			// oversize the order.
			const rawSize =
				targetNotional > 0 && referencePrice > 0
					? roundSizeToStep(targetNotional / referencePrice, szDecimals, "down")
					: 0;
			const size = maxSize > 0 ? Math.min(rawSize, maxSize) : rawSize;
			orderValue = size * referencePrice;
			displayQuoteAmount =
				orderValue > 0 ? formatPlainNumber(orderValue, 2) : "";
			displayAmount = size > 0 ? formatPlainNumber(size, szDecimals) : "";
			break;
		}
		default: {
			displayAmount = draft.size.input;
			const sizeNum = toNumber(displayAmount);
			orderValue =
				sizeNum > 0 && referencePrice > 0 ? sizeNum * referencePrice : 0;
			displayQuoteAmount =
				orderValue > 0 ? formatPlainNumber(orderValue, 2) : "";
			displayPercentage = calculatePercentage(orderValue, maxNotional);
		}
	}

	const sizeNum = toNumber(displayAmount);
	// Buying-power guard shared by EVERY size source. `quote` clamps a 1-step
	// rounding overshoot and `percent` floors to fit, but raw-size ("base") input
	// had no guard — a typed size above buying power passed `canSubmit` and the
	// exchange rejected it ("Insufficient margin", the bulk of order failures).
	// Block when >1 step over the max affordable size (same tolerance as `quote`).
	if (maxNotional > 0 && referencePrice > 0 && sizeNum > 0) {
		const factor = 10 ** szDecimals;
		if (Math.round(sizeNum * factor) - Math.round(maxSize * factor) > 1) {
			aboveMaxNotional = true;
		}
	}
	const marginRequired =
		selectedLeverage > 0 ? orderValue / selectedLeverage : 0;
	const estimatedLiquidation = estimateLiquidationPrice({
		side,
		orderSize: sizeNum,
		entryPrice: referencePrice,
		markPrice,
		selectedLeverage,
		maxLeverage,
		marginMode,
		perpAccountValue: toNumber(perpAccountValue),
		positionSize: toNumber(currentPositionSize),
		positionEntryPx: toNumber(currentPositionEntryPx),
		fallbackLiquidationPx: toNumber(currentPositionLiquidationPx),
	});
	const hasPrice =
		isMarketOrderType(orderType) ||
		isTwapOrderType(orderType) ||
		explicitPrice > 0;
	const hasSize = sizeNum > 0;

	return {
		displayPrice,
		displayAmount,
		displayQuoteAmount,
		displayPercentage,
		explicitPrice,
		referencePrice,
		sizeNum,
		orderValue,
		marginRequired,
		estimatedLiquidation,
		hasPrice,
		hasSize,
		aboveMaxNotional,
	};
}
